import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ScanLine, RefreshCw, AlertCircle, CheckCircle2,
  ImageOff, ChevronRight, Loader2,
} from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useOcrReviewList, type OcrResult } from '@/hooks/useOcr';
import { OcrReviewDialog } from '@/components/ocr/OcrReviewDialog';
import { ConfidencePill } from '@/components/ocr/ConfidencePill';

const STATUS_TABS: Array<{ key: string; statuses: string[] }> = [
  { key: 'all',           statuses: ['review_needed', 'failed'] },
  { key: 'review_needed', statuses: ['review_needed'] },
  { key: 'failed',        statuses: ['failed'] },
];

export default function OcrReviewPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [activeId, setActiveId] = useState<string | null>(null);

  const tab = STATUS_TABS.find((s) => s.key === activeTab) || STATUS_TABS[0];
  const { data, isLoading, refetch, isFetching } = useOcrReviewList({ status: tab.statuses });

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground -mb-2">
        <ArrowLeft className="h-4 w-4" /> {t('common.backToDashboard', 'Back to dashboard')}
      </Link>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-500/10 p-2 text-amber-600 dark:text-amber-400">
            <ScanLine className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-medium tracking-tight">{t('ocr.review.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('ocr.review.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Link to="/dashboard/ocr-settings">
            <Button variant="outline" size="sm">{t('ocr.settings.short')}</Button>
          </Link>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-md bg-muted p-1">
        {STATUS_TABS.map((s) => {
          const isActive = activeTab === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setActiveTab(s.key)}
              className={`rounded px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
                isActive
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`ocr.review.tab_${s.key}`)}
            </button>
          );
        })}
      </div>

      {/* Stats banner */}
      {data && data.total > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            {t('ocr.review.summary', {
              total: data.total,
              review: data.pending_review,
            })}
          </span>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-2">
          {data.data.map((row: OcrResult) => (
            <button
              key={row.id}
              onClick={() => setActiveId(row.id)}
              className="w-full rounded-lg border border-border bg-card p-4 text-start transition-colors hover:bg-muted/50"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <StatusIcon status={row.status} />
                  <div>
                    <div className="font-medium">{row.trip_code || '—'}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.trip_date} · {row.trip_site_name || '—'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {row.m3_value && (
                    <FieldChip label="m³" value={row.m3_value} confidence={row.m3_confidence} />
                  )}
                  {row.voucher_number && (
                    <FieldChip label="#" value={row.voucher_number} confidence={row.voucher_confidence} />
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>

              {row.failure_reason && (
                <div className="mt-2 text-xs text-destructive">
                  {t(`ocr.failure_reason.${row.failure_reason}`)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <OcrReviewDialog
        ocrResultId={activeId}
        onClose={() => setActiveId(null)}
        onProcessed={() => {
          setActiveId(null);
          refetch();
        }}
      />
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'failed') {
    return <ImageOff className="h-5 w-5 text-destructive" />;
  }
  if (status === 'extracted' || status === 'confirmed') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  }
  return <AlertCircle className="h-5 w-5 text-amber-500" />;
}

interface FieldChipProps {
  label: string;
  value: string | null;
  confidence: string | null;
}

function FieldChip({ label, value, confidence }: FieldChipProps) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
      <ConfidencePill confidence={confidence} compact />
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-16 text-center">
      <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
      <h3 className="text-base font-medium">{t('ocr.review.empty_title')}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('ocr.review.empty_subtitle')}
      </p>
    </div>
  );
}
